/**
 * How far a full-bleed element runs past the edge of the paper.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **To add a bleed, add a row to the table below.** The type, the list of ids
 * and every picker follow from it.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Bleed exists because paper is cut inaccurately. A background meant to reach
 * the edge, drawn to exactly the edge, shows a white hairline down one side
 * once the guillotine lands a fraction outside the trim line. The fix, and it
 * is the only fix, is to draw further than the page and let the cut fall
 * inside the ink.
 *
 * So this is deliberately not a general setting for "how big is the page". It
 * moves nothing that a reader sees. It only says how far past the trim the
 * things that already reach the trim should carry on, and everything in that
 * strip is expected to be thrown away.
 *
 * **What is in the bleed is lost.** Nothing may be put there that anybody
 * needs to read: no page number, no logo, no small print. The pictures and
 * washes that reach the edge extend into it, and that is all.
 *
 * Pure. Measurements and names, no rendering.
 */

import { parseMeasure, type MeasureRule } from '@/domain/layout/measure'

/**
 * The offered depths.
 *
 * Three millimetres is what commercial printing asks for and what nearly every
 * house style specifies. Five is for a heavier stock or a bindery known to
 * drift. There is no ten: past five, the amount being thrown away starts to
 * change how a picture has to be composed, which is a design decision rather
 * than a production allowance.
 */
export const BLEEDS = {
  /** The page ends at the page. Right for a report that is printed on an
      office machine, which cannot print to the edge in any case. */
  none: '0mm',
  narrow: '3mm',
  wide: '5mm',
} as const

/**
 * What a bleed may measure.
 *
 * Past five millimetres the amount being thrown away starts to change how a
 * picture has to be composed, which is a design decision rather than a
 * production allowance. Twenty is the point past which it is not a bleed.
 */
export const BLEED_RULE: MeasureRule = { unit: 'mm', min: 0, max: 20, step: 0.5 }

export type Bleed = keyof typeof BLEEDS

export const BLEED_IDS = Object.keys(BLEEDS) as readonly Bleed[]

export const DEFAULT_BLEED: Bleed = 'none'

/** Whether anything is being carried past the trim at all. */
export function bleeds(bleed: string): boolean {
  return (parseMeasure(bleed) ?? 0) > 0
}
